import { get, post } from '@/lib/api';

export type PurchaseOrderStatus =
  | 'NEW'
  | 'CONFIRMED'
  | 'REJECTED'
  | 'SHIPPED'
  | 'DELIVERED'
  | 'CANCELLED';

export type DebtStatus = 'UNPAID' | 'PARTIAL' | 'PAID' | 'OVERDUE';
export type PaymentTerms = 'PREPAY' | 'ON_DELIVERY' | 'DEFERRED';

export interface OrderItem {
  id: string;
  productId: string | null;
  /** ID номенклатуры в учётной системе поставщика (1С) — для интеграций. */
  productExternalId: string | null;
  name: string;
  unit: string;
  productUnitId: string | null;
  /** ID единицы измерения в учётной системе поставщика (1С). */
  unitId: string | null;
  price: number; // копейки
  vatRate: number;
  quantity: number;
  lineTotal: number; // копейки
}

export interface OrderPayment {
  id: string;
  amount: number; // копейки
  paidAt: string;
  note: string | null;
}

export interface SupplierOrder {
  id: string;
  status: PurchaseOrderStatus;
  totalCost: number; // копейки
  comment: string | null;
  paymentTerms: PaymentTerms;
  dueDate: string | null;
  paidAmount: number; // копейки
  debtStatus: DebtStatus;
  paidAt: string | null;
  contactPhone: string | null;
  deliveryAddress: string | null;
  supplyId: string | null;
  createdAt: string;
  confirmedAt: string | null;
  deliveredAt: string | null;
  items: OrderItem[];
  establishment: { id: string; name: string; phone: string | null; address: string | null };
  createdBy: { id: string; name: string | null } | null;
  payments: OrderPayment[];
}

export interface DebtRow {
  establishmentId: string;
  name: string;
  phone: string | null;
  ordersCount: number;
  totalOrdered: number; // копейки
  currentDebt: number; // копейки
  overdueDebt: number; // копейки
  creditLimit: number | null;
  note: string | null;
  isBlocked: boolean;
}

export interface DebtTotals {
  currentDebt: number;
  overdueDebt: number;
  totalOrdered: number;
}

export type SupplierOrderAction = 'confirm' | 'reject' | 'ship';

export const listOrders = (params?: {
  status?: PurchaseOrderStatus;
  debtOnly?: boolean;
  establishmentId?: string;
  limit?: number;
}) => get<{ results: SupplierOrder[] }>('/orders', params);

export const getOrder = (id: string) => get<SupplierOrder>(`/orders/${id}`);

export const getNewOrdersCount = () => get<{ count: number }>('/orders/new-count');

export interface CustomerDetail {
  establishment: {
    establishmentId: string;
    name: string;
    phone: string | null;
    address: string | null;
    ordersCount: number;
    totalOrdered: number; // копейки
    currentDebt: number; // копейки
    overdueDebt: number; // копейки
    creditLimit: number | null;
    note: string | null;
    isBlocked: boolean;
  };
  orders: SupplierOrder[];
}

export const getCustomer = (establishmentId: string) =>
  get<CustomerDetail>(`/customers/${establishmentId}`);

export const setOrderStatus = (id: string, action: SupplierOrderAction) =>
  post<SupplierOrder>(`/orders/${id}/status`, { action });

export const addOrderPayment = (
  id: string,
  body: { amount: number; paidAt?: string; note?: string },
) => post<SupplierOrder>(`/orders/${id}/payments`, body);

export const listDebts = () =>
  get<{ results: DebtRow[]; totals: DebtTotals }>('/debts');

export const listCustomers = () =>
  get<{ results: DebtRow[]; totals: DebtTotals }>('/customers');
